import axios from 'axios';
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from './firebase';

const baseURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001/api';
const apiKey = process.env.REACT_APP_API_KEY || '';

// --------------------------------------------------------
// AXIOS INSTANCE
// --------------------------------------------------------
const api = axios.create({
  baseURL: baseURL,
});

// --- API VERSION PREFIX (NEW) ---
const V1 = '/v1';
// --- END API VERSION PREFIX ---

// --------------------------------------------------------
// HELPER FUNCTION
// --------------------------------------------------------

/**
 * Helper function to securely retrieve cookie value
 */
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Helper function to get auth token from cookie (used for protected routes)
const getAuthToken = () => {
    // Note: The previous logic used 'auth_token' from cookie.
    // The new logic suggested using localStorage.getItem('auth_token').
    // We will stick to the cookie approach for consistency with existing protected routes.
    return getCookie('auth_token'); 
};

// --------------------------------------------------------
// AXIOS INTERCEPTOR (For Protected Routes)
// --------------------------------------------------------

// Add an interceptor to inject the Authorization header for protected counseling routes
api.interceptors.request.use(
    (config) => {
        // Only apply token if the route is one of the protected counseling routes
        const protectedRoutes = [
            '/counselors/profile', '/counselors/availability', '/counselors/bookings', 
            '/counselor-blogs', '/bookings'
        ];
        
        const isProtectedCounselingRoute = protectedRoutes.some(route => 
            config.url.startsWith(`${V1}${route}`) || 
            config.url.startsWith(`${V1}/admin/counselors`)
        );

        if (isProtectedCounselingRoute) {
            const token = getAuthToken();
            if (token && !config.headers['Authorization']) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);


// --------------------------------------------------------
// FIREBASE AUTH METHODS
// --------------------------------------------------------

/**
 * Initialize invisible reCAPTCHA
 */
export const initRecaptcha = (containerId) => {
  if (!window.recaptchaVerifier) {
    window.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
      callback: () => console.log('reCAPTCHA resolved'),
      'expired-callback': () => {
        console.log('reCAPTCHA expired');
        window.recaptchaVerifier = null;
      },
    });
  }
  return window.recaptchaVerifier;
};

/**
 * Send OTP using Firebase
 */
export const sendFirebaseOTP = async (phoneNumber) => {
  try {
    const appVerifier = window.recaptchaVerifier;
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);

    window.confirmationResult = confirmationResult;
    return { success: true, message: 'OTP sent successfully' };
  } catch (error) {
    console.error('Error sending OTP:', error);
    throw error;
  }
};

/**
 * Verify OTP (Firebase)
 */
export const verifyFirebaseOTP = async (otp) => {
  try {
    const result = await window.confirmationResult.confirm(otp);
    const firebaseUser = result.user;
    const idToken = await firebaseUser.getIdToken();

    return { success: true, idToken, user: firebaseUser };
  } catch (error) {
    console.error('Error verifying OTP:', error);
    throw error;
  }
};

// --------------------------------------------------------
// AUTHENTICATION - MULTI-USER SYSTEM
// --------------------------------------------------------

/**
 * Student Signup
 */
export const studentSignup = async (userData) => {
  return api.post(`${V1}/auth/student/signup`, userData);
};

/**
 * Partner Signup
 */
export const partnerSignup = async (userData) => {
  return api.post(`${V1}/auth/partner/signup`, userData);
};

/**
 * College Signup
 */
export const collegeSignup = async (userData) => {
  return api.post(`${V1}/auth/college/signup`, userData);
};

/**
 * Unified Login (checks all user types)
 */
export const login = async (email, password, userType) => {
  return api.post(
    `${V1}/auth/login?type=${userType}`,
    { email, password }
  );
};


/**
 * Resend verification email
 */
/**
 * Resend verification email
 */
export const resendVerificationEmail = async (params) => {
  // 🟢 FIXED: Changed path from '/users/...' to '/auth/...' to match routes.go
  return api.post(`${V1}/auth/resend-verification`, params);
};

// --------------------------------------------------------
// COUNSELING AND BOOKING ROUTES (NEW)
// --------------------------------------------------------

/**
 * Get all active counselors (with optional filters)
 */
export const getAllCounselors = (filters = {}) => {
  return api.get(`${V1}/counselors`, { params: filters });
};

/**
 * Get counselor by ID (public profile)
 */
export const getCounselorById = (counselorId) => {
  return api.get(`${V1}/counselors/${counselorId}`);
};

/**
 * Get counselor's public blogs
 */
export const getCounselorBlogs = (counselorId) => {
  return api.get(`${V1}/counselors/${counselorId}/blogs`);
};

/**
 * Counselor signup
 */
export const counselorSignup = (signupData) => {
  return api.post(`${V1}/counselors/signup`, signupData);
};

/**
 * Counselor login
 */
export const counselorLogin = (email, password) => {
  return api.post(`${V1}/counselors/login`, { email, password });
};

// --- PROTECTED COUNSELOR ENDPOINTS ---

/**
 * Get counselor's own profile
 */
export const getCounselorProfile = () => {
  return api.get(`${V1}/counselors/profile`);
};

/**
 * Update counselor profile
 */
export const updateCounselorProfile = (updateData) => {
  return api.put(`${V1}/counselors/profile`, updateData);
};

/**
 * Upload counselor photo
 */
export const uploadCounselorPhoto = (file) => {
  const formData = new FormData();
  formData.append('photo', file);
  
  // Note: Axios will automatically use the interceptor for Authorization.
  return api.post(`${V1}/counselors/profile/photo`, formData, {
    headers: {
        'Content-Type': 'multipart/form-data',
    }
  });
};

/**
 * Update availability
 */
export const updateAvailability = (slots) => {
  return api.put(`${V1}/counselors/availability`, { slots });
};

/**
 * Get counselor's bookings
 */
export const getCounselorBookings = (status = null) => {
  return api.get(`${V1}/counselors/bookings`, { 
    params: status ? { status } : {} 
  });
};

/**
 * Update booking status
 */
export const updateBookingStatus = (bookingId, status, notes = '') => {
  return api.put(`${V1}/counselors/bookings/${bookingId}/status`, { status, notes });
};

// --- BLOG MANAGEMENT ENDPOINTS ---

/**
 * Create new blog post
 */
export const createBlog = (blogData) => {
  return api.post(`${V1}/counselor-blogs`, blogData);
};

/**
 * Get counselor's own blogs
 */
export const getMyCounselorBlogs = () => {
  return api.get(`${V1}/counselor-blogs/my-blogs`);
};

/**
 * Update blog
 */
export const updateBlog = (blogId, updateData) => {
  return api.put(`${V1}/counselor-blogs/${blogId}`, updateData);
};

/**
 * Delete blog
 */
export const deleteBlog = (blogId) => {
  return api.delete(`${V1}/counselor-blogs/${blogId}`);
};

// --- STUDENT BOOKING ENDPOINTS ---

/**
 * Create booking (Student)
 */
export const createBooking = (bookingData) => {
  return api.post(`${V1}/bookings`, bookingData);
};

/**
 * Get student's bookings
 */
export const getMyBookings = () => {
  return api.get(`${V1}/bookings/my-bookings`);
};

/**
 * Cancel booking
 */
export const cancelBooking = (bookingId) => {
  return api.put(`${V1}/bookings/${bookingId}/cancel`);
};

// --- ADMIN ENDPOINTS ---

/**
 * Get pending counselors (Admin)
 */
export const getPendingCounselors = () => {
  return api.get(`${V1}/admin/counselors/pending`);
};

/**
 * Approve counselor (Admin)
 */
export const approveCounselor = (counselorId) => {
  return api.put(`${V1}/admin/counselors/${counselorId}/approve`);
};

/**
 * Reject counselor (Admin)
 */
export const rejectCounselor = (counselorId) => {
  return api.put(`${V1}/admin/counselors/${counselorId}/reject`);
};


// --------------------------------------------------------
// EMAIL VERIFICATION (LINK-BASED)
// --------------------------------------------------------

/**
 * Send email verification link
 */
export const sendEmailVerification = (email) => {
  return api.post(`${V1}/users/verify-email`, { email });
};

/**
 * Check verification status
 */
export const checkEmailVerification = (email) => {
  return api.get(`${V1}/users/check-verification?email=${email}`);
};

// --------------------------------------------------------
// STUDENT DASHBOARD ROUTES
// --------------------------------------------------------

/**
 * Update the student's profile details
 */
export const updateStudentProfile = (studentId, profileData) => {
  return api.put(`${V1}/student/${studentId}/profile`, profileData);
};

/**
 * Get payment history for a student
 */
export const getStudentPayments = (studentId) => {
  return api.get(`${V1}/payments/student/${studentId}`);
};

// --------------------------------------------------------
// CAROUSEL PUBLIC ROUTE (NEW)
// --------------------------------------------------------

/**
 * Fetches all active carousel items (Public).
 */
export const getCarouselItems = () => {
    // Uses the base URL (which includes /api) + the v1 path
    return api.get(`${V1}/carousel`);
};

// --------------------------------------------------------
// COLLEGE PUBLIC ROUTES - ENHANCED WITH SEARCH
// --------------------------------------------------------

/**
 * Get colleges with pagination and filters
 * @param {Object} params - Filter parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Results per page
 * @param {string} params.courseName - Search by course name
 * @param {string} params.city - Filter by city
 * @param {string} params.state - Filter by state
 * @param {string} params.sortBy - Sort field (fees, rating, name)
 * @param {string} params.sortOrder - Sort order (asc, desc)
 * @param {string} params.tuitionFee - Fee range "min,max"
 * @param {string} params.universityTypes - Comma-separated types
 * @param {string} params.states - Comma-separated states
 * @param {string} params.durations - Comma-separated durations
 */
export const getColleges = (params) => {
  return api.get(`${V1}/colleges`, { params });
};

/**
 * Search colleges by course name
 */
export const searchCollegesByCourse = (courseName, filters = {}) => {
  return api.get(`${V1}/colleges`, { 
    params: { 
      courseName,
      ...filters 
    } 
  });
};

/**
 * Search colleges by location
 */
export const searchCollegesByLocation = (city, state, filters = {}) => {
  const params = { ...filters };
  if (city) params.city = city;
  if (state) params.state = state;
  return api.get(`${V1}/colleges`, { params });
};

/**
 * Get colleges sorted by fees (low to high or high to low)
 */
export const getCollegesSortedByFees = (order = 'asc', filters = {}) => {
  return api.get(`${V1}/colleges`, {
    params: {
      sortBy: 'fees',
      sortOrder: order,
      ...filters
    }
  });
};

/**
 * Get colleges sorted by rating
 */
export const getCollegesSortedByRating = (order = 'desc', filters = {}) => {
  return api.get(`${V1}/colleges`, {
    params: {
      sortBy: 'rating',
      sortOrder: order,
      ...filters
    }
  });
};

/**
 * Advanced college search with multiple filters
 */
export const advancedCollegeSearch = ({
  courseName,
  city,
  state,
  minFee,
  maxFee,
  sortBy = 'name',
  sortOrder = 'asc',
  page = 1,
  limit = 10
}) => {
  const params = {
    page,
    limit,
    sortBy,
    sortOrder
  };

  if (courseName) params.courseName = courseName;
  if (city) params.city = city;
  if (state) params.state = state;
  if (minFee !== undefined && maxFee !== undefined) {
    params.tuitionFee = `${minFee},${maxFee}`;
  }

  return api.get(`${V1}/colleges`, { params });
};

/**
 * Get college by ID or name
 * FIX: Manually encode the ID/Name to correctly handle spaces and special characters in the path segment.
 */
export const getCollegeById = (idOrName) => {
  return api.get(`${V1}/colleges/${encodeURIComponent(idOrName)}`);
};

/**
 * Search colleges (general search)
 */
export const searchColleges = (params) => {
  return api.get(`${V1}/colleges/search`, { params });
};

/**
 * Get college statistics
 */
export const getCollegeStats = () => {
  return api.get(`${V1}/colleges/stats/summary`);
};

/**
 * Submit contact form
 */
export const submitContactForm = (contactData) => {
  return api.post(`${V1}/contact`, contactData);
};

// --------------------------------------------------------
// PROTECTED COLLEGE ROUTES (Api-Key) - FOR ADMIN
// --------------------------------------------------------

export const createCollege = (formData) => {
  return api.post(`${V1}/colleges`, formData, {
    headers: {
      'X-Api-Key': apiKey,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const updateCollege = (id, formData) => {
  return api.put(`${V1}/colleges/${id}`, formData, {
    headers: {
      'X-Api-Key': apiKey,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const deleteCollege = (id) => {
  return api.delete(`${V1}/colleges/${id}`, {
    headers: {
      'X-Api-Key': apiKey,
    },
  });
};

// --------------------------------------------------------
// COLLEGE SELECTION ROUTES
// --------------------------------------------------------

/**
 * Select multiple colleges
 */
export const selectColleges = (selectionData) => {
  return api.post(`${V1}/selections/select`, selectionData);
};

/**
 * Get selection by ID
 */
export const getSelection = (selectionId) => {
  return api.get(`${V1}/selections`, { 
    params: { id: selectionId } 
  });
};

/**
 * Update selection status
 */
export const updateSelectionStatus = (selectionId, status) => {
  return api.put(`${V1}/selections/status`, 
    { status },
    { params: { id: selectionId } }
  );
};

// --------------------------------------------------------
// DRAFT SELECTION ROUTES
// --------------------------------------------------------

/**
 * Save current draft selection (requires auth token from cookie)
 */
/**
 * Save current draft selection (requires auth token from cookie)
 */
export const saveDraftSelection = async (payload) => {
  try {
    const token = getCookie('auth_token');
    
    if (!token) {
      throw new Error('Authentication token not found. Please login again.');
    }

    // FIXED: Pass 'payload' directly. Do not wrap it in { colleges: ... }
    const response = await api.post(`${V1}/selections/draft`, 
      payload,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response;
  } catch (error) {
    console.error('Save draft error:', error);
    throw error;
  }
};

/**
 * Get all draft selections for a user
 */
export const getDraftSelections = async (userId) => {
  try {
    const token = getCookie('auth_token');
    
    if (!token) {
      throw new Error('Authentication token not found. Please login again.');
    }

    const response = await api.get(`${V1}/selections/draft/${userId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response;
  } catch (error) {
    console.error('Get draft error:', error);
    throw error;
  }
};

/**
 * Delete a specific draft selection
 */
export const deleteDraftSelection = async (draftId) => {
  try {
    const token = getCookie('auth_token');
    
    if (!token) {
      throw new Error('Authentication token not found. Please login again.');
    }

    const response = await api.delete(`${V1}/selections/draft/${draftId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response;
  } catch (error) {
    console.error('Delete draft error:', error);
    throw error;
  }
};

// --------------------------------------------------------
// PAYMENT ROUTES
// --------------------------------------------------------

/**
 * Initiate payment for selected colleges
 */
export const initiatePayment = (paymentData) => {
  return api.post(`${V1}/payments/create-order`, paymentData);
};

/**
 * Verify payment after Razorpay callback
 */
export const verifyPayment = (verificationData) => {
  return api.post(`${V1}/payments/verify`, verificationData);
};

/**
 * Get payment by ID
 */
export const getPayment = (paymentId) => {
  return api.get(`${V1}/payments`, { 
    params: { id: paymentId } 
  });
};

/**
 * Get all payments for a user
 */
export const getUserPayments = (userId) => {
  return api.get(`${V1}/payments/user`, { 
    params: { userId } 
  });
};

// --------------------------------------------------------
// RAZORPAY INTEGRATION HELPERS
// --------------------------------------------------------

/**
 * Load Razorpay checkout script
 */
export const loadRazorpayScript = () => {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
    document.body.appendChild(script);
  });
};

/**
 * Display Razorpay checkout
 */
export const displayRazorpayCheckout = async (options, onSuccess, onFailure) => {
  try {
    await loadRazorpayScript();
    
    const razorpayOptions = {
      key: options.razorpayKey,
      amount: options.amount * 100,
      currency: options.currency || 'INR',
      name: 'College Selection',
      description: 'Payment for selected colleges',
      order_id: options.orderId,
      handler: function (response) {
        onSuccess(response);
      },
      prefill: {
        name: options.userName,
        email: options.userEmail,
        contact: options.userPhone,
      },
      theme: {
        color: '#3B82F6',
      },
      modal: {
        ondismiss: function() {
          onFailure(new Error('Payment cancelled by user'));
        }
      }
    };

    const rzp = new window.Razorpay(razorpayOptions);
    rzp.open();
  } catch (error) {
    onFailure(error);
  }
};

// --------------------------------------------------------
// FILTER HELPER FUNCTIONS
// --------------------------------------------------------

/**
 * Get available states from backend
 */
export const getAvailableStates = async () => {
  try {
    const response = await api.get(`${V1}/colleges/filters/states`);
    return response.data;
  } catch (error) {
    console.error('Error fetching states:', error);
    return [];
  }
};

/**
 * Get available cities by state
 */
export const getCitiesByState = async (state) => {
  try {
    const response = await api.get(`${V1}/colleges/filters/cities?state=${state}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching cities:', error);
    return [];
  }
};

/**
 * Get available course names
 */
export const getAvailableCourses = async () => {
  try {
    const response = await api.get(`${V1}/colleges/filters/courses`);
    return response.data;
  } catch (error) {
    console.error('Error fetching courses:', error);
    return [];
  }
};

// --------------------------------------------------------
// ADMIN ROUTES (With API Key)
// --------------------------------------------------------

export const fetchAllSignups = () => {
  return api.get(`${V1}/admin/signups`, {
    headers: {
      'X-Api-Key': apiKey,
    },
  });
};

export default api;